import { addSlideFooter } from '../slide_footer';
import { FONT_FACE, LINK_COLOR } from '../constants';
import { SEARCH_PARAM_KEYS } from '../../../../../../../../../../../pages/Survey/utilities/constants';

/**
 * Gets metadata text for the slide
 * @param {Object} options
 * @returns {string}
 */
const getMetadataText = ({
  appliedFilters,
  compareBy,
  questionsCount,
  quotaGroupsCount,
  totalResponses,
}) => {
  const downloadDate = new Intl.DateTimeFormat('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date());

  const metadataLines = [
    `Total Responses: ${totalResponses}`,
    `Downloaded: ${downloadDate}`,
    `Questions: ${questionsCount}`,
    `Quota groups: ${quotaGroupsCount}`,
  ];

  const displayCompareBy =
    compareBy && compareBy !== 'overall' && compareBy.trim() !== '' ? compareBy : 'Overall';
  metadataLines.push(`Compared by: ${displayCompareBy}`);

  const displayFilters =
    appliedFilters && appliedFilters.length > 0 ? appliedFilters.join(', ') : 'None';
  metadataLines.push(`Filtered by: ${displayFilters}`);

  return metadataLines.join('\n');
};

/**
 * Gets applied filters with question details for PowerPoint metadata
 * @param {Object} options - Configuration options
 * @param {Object} options.globalFilters - Global filters object
 * @param {Array} options.questionsRef - Alternative questions reference
 * @param {URLSearchParams} options.searchParams - URL search parameters
 * @param {Object} options.survey - Survey object with quota groups
 * @param {Array} options.surveyQuestions - Survey questions for question details
 * @returns {Array} Array of filter descriptions
 */
const getAppliedFilters = ({
  globalFilters,
  questionsRef = [],
  searchParams,
  survey = null,
  surveyQuestions = [],
}) => {
  const filters = [];

  if (!searchParams || !globalFilters) return filters;

  const quotaGroupFilter = searchParams.get(SEARCH_PARAM_KEYS.QUOTA_GROUP);
  let selectedGroupIds = [];
  if (quotaGroupFilter) {
    selectedGroupIds = quotaGroupFilter.split(',').filter(Boolean);
  } else if (globalFilters?.quotaGroups) {
    selectedGroupIds = globalFilters.quotaGroups.map(
      ({ instantSurveyGroupRef }) => instantSurveyGroupRef
    );
  }

  if (selectedGroupIds.length > 0 && survey?.groups && Array.isArray(survey.groups)) {
    const selectedGroups = [];
    selectedGroupIds.forEach((groupId) => {
      const group = survey.groups.find((g) => g && String(g.id) === String(groupId));
      if (group && group.name) {
        selectedGroups.push(group.name);
      }
    });

    if (selectedGroups.length > 0) {
      selectedGroups.forEach((groupName) => {
        filters.push(groupName);
      });
    }
  }

  if (globalFilters?.demographicFilters) {
    globalFilters.demographicFilters.forEach(({ id, label }) => {
      const filterValue = searchParams.get(id);
      if (filterValue && filterValue.split(',').filter(Boolean).length > 0) {
        filters.push(label);
      }
    });
  }

  const questionOptionFilter = searchParams.get(SEARCH_PARAM_KEYS.QUESTION_OPTION);
  if (questionOptionFilter && questionOptionFilter.split(',').filter(Boolean).length > 0) {
    const selectedOptionIds = questionOptionFilter.split(',').filter(Boolean);
    const filteredQuestions = new Set();

    const questionsToCheck =
      Array.isArray(surveyQuestions) && surveyQuestions.length > 0
        ? surveyQuestions
        : Array.isArray(questionsRef) && questionsRef.length > 0
          ? questionsRef
          : [];

    if (questionsToCheck.length > 0) {
      selectedOptionIds.forEach((optionId) => {
        questionsToCheck.forEach((question, index) => {
          if (question && question.options && Array.isArray(question.options)) {
            const hasOption = question.options.some(
              (option) => option && String(option.id) === String(optionId)
            );
            if (hasOption) {
              filteredQuestions.add(`Q ${index + 1}`);
            }
          }
        });
      });
    }

    if (filteredQuestions.size > 0) {
      const questionsList = Array.from(filteredQuestions).sort().join(', ');
      filters.push(questionsList);
    } else {
      filters.push('Question Options');
    }
  }

  return filters;
};

/**
 * Creates metadata slide for survey dashboard PowerPoint export
 * @param {Object} options
 * @param {Object} options.pptx - PptxGenJS instance
 * @param {string} options.surveyResponsesUrl - URL to survey responses
 * @param {Object} options.details - Survey details containing quota group info
 * @param {Object} options.survey - Survey object
 * @param {Object} options.insightsDocument - Insights document object
 * @param {Array} options.surveyQuestions - Array of survey questions (for fallback)
 * @param {Array} options.questionsRef - Reference to questions (survey.questions or insightsDocument.questions)
 * @param {Object} options.selectedCompareBy - Selected compare by option
 * @param {URLSearchParams} options.searchParams - URL search parameters for filters
 * @param {Object} options.globalFilters - Global filters object
 * @param {string} options.reportType - Report type
 * @param {string} options.reportUrl - Report URL
 * @returns {Object} Created slide
 */
export const createMetadataSlide = ({
  details,
  globalFilters,
  insightsDocument,
  pptx,
  questionsRef,
  reportType,
  reportUrl,
  searchParams,
  selectedCompareBy,
  survey,
  surveyQuestions,
  surveyResponsesUrl,
  surveyTitle,
}) => {
  const slide = pptx.addSlide();

  const quotaGroupsCount = survey?.groups?.length > 1 ? survey.groups.length : 1;
  const totalResponses = details?.completes || insightsDocument?.completed || 0;
  const questionsCount = questionsRef?.length || surveyQuestions?.length || 0;
  const compareBy = selectedCompareBy?.label || '';
  const appliedFilters = getAppliedFilters({
    globalFilters,
    questionsRef,
    searchParams,
    survey,
    surveyQuestions,
  });

  const metadataText = getMetadataText({
    appliedFilters,
    compareBy,
    questionsCount,
    quotaGroupsCount,
    surveyResponsesUrl,
    surveyTitle,
    totalResponses,
  });

  slide.addText(
    [
      { text: 'Source: ' },
      {
        options: { hyperlink: { url: surveyResponsesUrl } },
        text: surveyTitle,
      },
    ],
    {
      align: 'left',
      color: LINK_COLOR,
      fontFace: FONT_FACE,
      fontSize: 10,
      italic: true,
      lineSpacing: 14,
      valign: 'top',
      w: '90%',
      x: 0.5,
      y: 1.0,
    }
  );

  slide.addText(metadataText, {
    align: 'left',
    color: LINK_COLOR,
    fontFace: FONT_FACE,
    fontSize: 10,
    italic: true,
    lineSpacing: 14,
    valign: 'top',
    w: '90%',
    x: 0.5,
    y: 1.2,
  });

  addSlideFooter({ pptx, reportType, reportUrl, slide });

  return slide;
};
